import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Shield, Plus, Trash2, Lock, Check, Loader2, Pencil, ChevronDown, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

// Blank draft for the create/edit modal.
const emptyDraft = () => ({ key: null, displayName: '', description: '', sections: [], isProtected: false, userCount: 0 });

export default function RolesPage() {
  const toast = useToast();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null); // null = modal closed
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState({}); // role key -> show assigned users
  const toggleExpanded = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  const load = () => {
    setLoading(true);
    return adminApi.listRoles()
      .then((res) => { setRoles(res.roles || []); setCatalog(res.catalog || []); })
      .catch((err) => toast.error(`Failed to load roles: ${err.message}`))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Catalog grouped by its `group` for a tidy matrix.
  const grouped = useMemo(() => {
    const g = {};
    for (const s of catalog) (g[s.group] = g[s.group] || []).push(s);
    return Object.entries(g);
  }, [catalog]);

  const openCreate = () => { setDraft(emptyDraft()); setConfirmDelete(false); };
  const openEdit = (r) => { setDraft({ ...r, sections: [...r.sections] }); setConfirmDelete(false); };

  const toggleSection = (key) => setDraft((d) => ({
    ...d,
    sections: d.sections.includes(key) ? d.sections.filter((s) => s !== key) : [...d.sections, key],
  }));

  const handleSave = async () => {
    if (!draft.displayName.trim()) { toast.error('Role name is required'); return; }
    setBusy(true);
    try {
      await adminApi.upsertRole({
        key: draft.key || undefined,
        displayName: draft.displayName.trim(),
        description: draft.description.trim(),
        sections: draft.sections,
      });
      toast.success(draft.key ? 'Role updated' : 'Role created');
      setDraft(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await adminApi.deleteRole(draft.key);
      toast.success('Role deleted');
      setDraft(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(false); }
  };

  const labelFor = (key) => catalog.find((s) => s.key === key)?.label || key;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" /> Roles &amp; Permissions
          </h1>
          <p className="text-muted-foreground">
            Create roles and choose which sections each can access. Assign people to roles under People.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> New role
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roles.map((r) => (
            <Card key={r.key}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-1.5">
                      {r.displayName}
                      {r.isProtected && <Lock className="h-3.5 w-3.5 text-muted-foreground" title="Protected — always full access" />}
                    </div>
                    {r.description && <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>}
                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
                      {r.userCount > 0 ? (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(r.key)}
                          className="inline-flex items-center gap-1 rounded hover:text-foreground transition-colors"
                          title="Show assigned users"
                        >
                          <Users className="h-3 w-3" />
                          {r.userCount} user{r.userCount === 1 ? '' : 's'}
                          <ChevronDown className={cn('h-3 w-3 transition-transform', expanded[r.key] && 'rotate-180')} />
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> 0 users</span>
                      )}
                      <span>·</span>
                      <span className="font-mono">{r.key}</span>
                    </div>
                    {expanded[r.key] && r.userCount > 0 && (
                      <ul className="mt-2 space-y-0.5 rounded-md border border-border bg-muted/30 p-2.5">
                        {(r.users || []).map((u, i) => (
                          <li key={i} className="text-xs text-foreground flex items-baseline gap-1.5">
                            <span className="text-muted-foreground">•</span>
                            <span className="font-medium">{u.displayName}</span>
                            {u.email && <span className="text-muted-foreground">— {u.email}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(r)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {r.isProtected ? (
                    <Badge variant="success">All sections</Badge>
                  ) : r.sections.length === 0 ? (
                    <span className="text-xs text-muted-foreground">No admin sections</span>
                  ) : (
                    r.sections.map((s) => <Badge key={s} variant="default">{labelFor(s)}</Badge>)
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {draft && (
        <Modal isOpen onClose={() => setDraft(null)} title={draft.key ? `Edit role — ${draft.displayName}` : 'New role'} width="min(680px, 96vw)">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Role name" value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} placeholder="e.g. HR Manager" />
              <Input label="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Optional" />
            </div>

            {draft.isProtected ? (
              <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground flex items-center gap-2">
                <Lock className="h-4 w-4" /> This role always has full access and can't be restricted.
              </div>
            ) : (
              <div>
                <div className="text-sm font-medium mb-2">Sections this role can access</div>
                <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                  {grouped.map(([group, items]) => (
                    <div key={group}>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{group}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {items.map((s) => {
                          const on = draft.sections.includes(s.key);
                          return (
                            <button
                              key={s.key}
                              type="button"
                              onClick={() => toggleSection(s.key)}
                              className={cn('flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm', on ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent')}
                            >
                              <span className={cn('h-4 w-4 rounded border flex items-center justify-center shrink-0', on ? 'border-primary bg-primary text-white' : 'border-border')}>
                                {on && <Check className="h-3 w-3" />}
                              </span>
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-1 border-t border-border">
              <div>
                {draft.key && !draft.isProtected && (
                  !confirmDelete ? (
                    <Button variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)} disabled={busy}>
                      <Trash2 className="h-4 w-4" /> Delete role
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-destructive font-medium">Delete this role?</span>
                      <Button variant="danger" onClick={handleDelete} loading={busy}>Yes, delete</Button>
                      <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>Cancel</Button>
                    </div>
                  )
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => setDraft(null)} disabled={busy}>Cancel</Button>
                <Button onClick={handleSave} loading={busy} disabled={busy}>Save</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
